import { MoonStar } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

const frequencyOptions = [
  "Nikad",
  "Veoma retko",
  "Retko",
  "Ponekad",
  "Često",
  "Skoro uvek",
];

const increasingScores = [0, 1, 2, 3, 4, 5];

export const testNesanice: QuizConfig = {
  id: "test-nesanice",
  illustration: {
    src: "/images/quizzes/test-nesanice.webp",
    alt: "Žena leži budna u krevetu dok kroz prozor dopire mesečina.",
  },
  lastModified: "2026-07-31",
  title: "Test nesanice: Koliko ti problemi sa snom remete dan?",
  shortTitle: "Test nesanice",
  description:
    "Test nesanice procenjuje teškoće sa uspavljivanjem, buđenjem i dnevnim funkcionisanjem. Služi za samoprocenu, ne za dijagnozu.",
  longDescription:
    "Ovaj test nesanice kroz 11 pitanja sagledava uspavljivanje, noćna i rana buđenja, brigu zbog sna i uticaj odmora na svakodnevni život. Rezultat pokazuje koliko su trenutno izražene teškoće sa spavanjem. Test služi za samoprocenu i ne postavlja dijagnozu.",
  icon: MoonStar,
  duration: "2-3 min",
  category: "Mentalno zdravlje",
  focusAreasWanted: [
    "sleep_problems",
    "anxiety",
    "emotional_regulation",
    "stress",
  ],
  featured: false,
  promptExtension: `
    Ovo je originalna, nevalidirana samoprocena teškoća sa spavanjem, nije dijagnostički niti klinički skrining test. Oblasti su informisane opštim kliničkim opisima nesanice i njenog uticaja na dnevno funkcionisanje, bez reprodukovanja zaštićenog upitnika: https://doi.org/10.1111/jsr.14035

    Tumači ukupni rezultat ovako:
    - 0-39: trenutno je prisutno manje pokazatelja teškoća sa spavanjem
    - 40-60: prisutni su umereni pokazatelji koje vredi pratiti u kontekstu
    - 61-100: izraženi pokazatelji mogu uticati na odmor ili dnevno funkcionisanje

    Ovi rasponi su interne, nekliničke smernice proizvoda. Nisu validirani pragovi i ne smeju se koristiti za dijagnozu.

    Obavezna pravila:
    - Rezultat opisuj kao pokazatelj ili tendenciju, nikada kao dijagnozu ili dokaz da osoba ima nesanicu.
    - Ne zaključuj uzrok problema sa snom. Stres, raspoloženje, zdravstveno stanje, lekovi, smenski rad i navike mogu imati različite uloge koje ovaj test ne procenjuje.
    - Za rezultat iznad 60 ili odgovore koji pokazuju izražen uticaj na svakodnevni život preporuči razgovor sa lekarom, psihologom ili psihoterapeutom.
    - Ne preporučuj lekove, suplemente, doze niti prekid propisane terapije.
    - Predloži samo niskorizične korake: beleženje sna, doslednije vreme ustajanja, smanjenje vremena budnosti u krevetu i razgovor sa stručnjakom ako teškoće traju ili se pogoršavaju.
  `,
  questions: [
    {
      type: "radio",
      question:
        "Tokom poslednje dve nedelje, koliko često ti je trebalo dugo da zaspiš?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često si se tokom noći budio/la i teško ponovo zaspao/la?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često si se probudio/la ranije nego što želiš i nisi mogao/la da nastaviš da spavaš?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često si pre odlaska u krevet brinuo/la da ponovo nećeš dobro spavati?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question: "Koliko često si bio/la nezadovoljan/na kvalitetom svog sna?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često si se tokom dana osećao/la umorno ili neodmoreno zbog lošeg sna?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često ti je zbog lošeg sna bilo teško da se koncentrišeš ili završiš obaveze?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često je loš san uticao na tvoje raspoloženje ili strpljenje sa drugima?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često si menjao/la planove ili izbegavao/la aktivnosti zato što nisi bio/la odmoran/na?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često su ti teškoće sa spavanjem remetile posao, učenje, odnose ili druge dnevne obaveze?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "textarea",
      question: "Opiši kako nesanica utiče na tvoj svakodnevni život:",
    },
  ],
  copy: {
    title: "Tvoja procena teškoća sa spavanjem",
    subtitle: "Razumevanje trenutnih problema sa snom",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Sledeći koraci za tebe",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  landing: {
    summary:
      "Odgovori na 11 kratkih pitanja o uspavljivanju, noćnim buđenjima i uticaju sna na tvoj dan. Dobićeš orijentacionu procenu trenutnih teškoća i smernice kada ima smisla da razgovaraš sa stručnjakom.",
    highlights: [
      {
        title: "Poslednje dve nedelje",
        description:
          "Procena se odnosi na skoriji period, pa je lakše da odgovoriš konkretno.",
      },
      {
        title: "San i svakodnevni život",
        description:
          "Pitanja povezuju uspavljivanje i buđenja sa energijom, koncentracijom i raspoloženjem.",
      },
      {
        title: "Jasan sledeći korak",
        description:
          "Rezultat objašnjava kada možeš da pratiš obrazac, a kada vredi uključiti stručnjaka.",
      },
    ],
    whoIsItFor:
      "Test je namenjen odraslima koji teško zaspe, bude se tokom noći, bude se prerano ili osećaju da im loš san remeti dan.",
    whatYouLearn:
      "Saznaćeš koliko su izražene tvoje trenutne teškoće sa spavanjem i koliko utiču na svakodnevno funkcionisanje.",
    howItWorks:
      "Za svako pitanje označi koliko često je važilo za tebe tokom poslednje dve nedelje. Rezultat je orijentaciona samoprocena, ne klinički skor.",
    nextSteps:
      "Ovo nije dijagnoza. Ako teškoće traju, pogoršavaju se ili remete dnevno funkcionisanje, razgovor sa lekarom, psihologom ili psihoterapeutom može pomoći da razjasniš uzrok i opcije podrške.",
    faq: [
      {
        question: "Da li ovaj test može da potvrdi nesanicu?",
        answer:
          "Ne. Test daje smernicu za samoprocenu trenutnih teškoća sa spavanjem. Dijagnozu može postaviti samo kvalifikovani zdravstveni stručnjak nakon šire procene.",
      },
      {
        question: "Da li je ovo validirani klinički upitnik?",
        answer:
          "Ne. Pitanja su originalna i pokrivaju česte teškoće sa spavanjem i njihov uticaj na dan. Rezultat nije zamena za validirani instrument niti stručnu procenu.",
      },
      {
        question: "Kada ima smisla da razgovaram sa stručnjakom?",
        answer:
          "Razmisli o razgovoru sa stručnjakom ako teškoće traju, pogoršavaju se ili remete energiju, koncentraciju, raspoloženje, posao ili odnose. Ne moraš da čekaš da problem postane težak.",
      },
    ],
    ctaLabel: "Uradi test nesanice",
  },
};
